import {
	CONSTELLATION_CENTER,
	CONSTELLATION_DOT_COUNT,
	CONSTELLATION_FORMATION_RADIUS,
	FULL_ROTATION_RADIANS,
} from '../constants';
import type { ConstellationDot } from '../types';

type InfinityDotInput = Readonly<{
	color: string;
	index: number;
	timeSeconds: number;
}>;

const STREAM_SPEED_RADIANS_PER_SECOND = 1.15;
const CREST_SPEED_RADIANS_PER_SECOND = 2.85;
const CREST_WIDTH_RADIANS = 0.55;
const HORIZONTAL_RADIUS_RATIO = 1.15;
const VERTICAL_TO_HORIZONTAL_RATIO = 1.25;
const VERTICAL_RADIUS_RATIO = HORIZONTAL_RADIUS_RATIO * VERTICAL_TO_HORIZONTAL_RATIO;
const BASE_BRIGHTNESS = 0.4;
const CREST_BRIGHTNESS = 0.6;

const wrapRadiansToPi = (angleRadians: number): number =>
	((((angleRadians + Math.PI) % FULL_ROTATION_RADIANS) + FULL_ROTATION_RADIANS) %
		FULL_ROTATION_RADIANS) -
	Math.PI;

const getInfinityPosition = (angleRadians: number): Readonly<{ x: number; y: number }> => {
	const sine = Math.sin(angleRadians);
	const cosine = Math.cos(angleRadians);
	const lemniscateDenominator = 1 + sine ** 2;

	// Bernoulli lemniscate: the vertical correction rounds each loop without
	// making the complete infinity mark taller than it is wide.
	return {
		x:
			CONSTELLATION_CENTER +
			(CONSTELLATION_FORMATION_RADIUS * HORIZONTAL_RADIUS_RATIO * cosine) / lemniscateDenominator,
		y:
			CONSTELLATION_CENTER +
			(CONSTELLATION_FORMATION_RADIUS * VERTICAL_RADIUS_RATIO * sine * cosine) /
				lemniscateDenominator,
	};
};

export const createInfinityDot = ({
	color,
	index,
	timeSeconds,
}: InfinityDotInput): ConstellationDot => {
	const streamAngleRadians =
		(index / CONSTELLATION_DOT_COUNT) * FULL_ROTATION_RADIANS +
		timeSeconds * STREAM_SPEED_RADIANS_PER_SECOND;
	const crestDistanceRadians = wrapRadiansToPi(
		streamAngleRadians - timeSeconds * CREST_SPEED_RADIANS_PER_SECOND,
	);

	// Gaussian packet: creates a bright crest that overtakes the moving stream.
	const crest = Math.exp(-((crestDistanceRadians / CREST_WIDTH_RADIANS) ** 2));

	return {
		...getInfinityPosition(streamAngleRadians),
		brightness: BASE_BRIGHTNESS + CREST_BRIGHTNESS * crest,
		color,
		glowStrength: crest,
		scale: 1,
	};
};
